'use client';
import React, { useState, useMemo } from 'react';
import { Search, Download, QrCode, History, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import CertVerifyModal from './CertVerifyModal';
import AuditTrailModal from './AuditTrailModal';

export const certificates = [
  {
    id: 'cert-001',
    certId: 'HC-MY-2026-004582',
    company: 'Nusantara Halal Foods Sdn Bhd',
    product: 'Premium Halal Frozen Beef',
    category: 'Halal Food & Beverage',
    certBody: 'JAKIM Recognized Partner',
    country: 'Malaysia',
    flag: '🇲🇾',
    issueDate: '15 Jan 2026',
    expiryDate: '31 Dec 2027',
    status: 'Valid',
    trustScore: 96,
    hash: '0xHLCM-9FA2-44BE-88D1',
    recognizedMarkets: ['Malaysia', 'UAE', 'Saudi Arabia', 'Indonesia'],
  },
  {
    id: 'cert-002',
    certId: 'HC-US-2026-001129',
    company: 'PureEthic Beauty LLC',
    product: 'Halal Certified Skincare Range',
    category: 'Cosmetics',
    certBody: 'IFANCA',
    country: 'United States',
    flag: '🇺🇸',
    issueDate: '03 Feb 2026',
    expiryDate: '28 Feb 2028',
    status: 'Valid',
    trustScore: 91,
    hash: '0xHLCM-3BC1-72AF-91E4',
    recognizedMarkets: ['Malaysia', 'Indonesia', 'GCC'],
  },
  {
    id: 'cert-003',
    certId: 'HC-CN-2026-008921',
    company: 'Zhejiang Halal Ingredients Co.',
    product: 'Halal Food Ingredients',
    category: 'Ingredients',
    certBody: 'China Halal Certification Partner',
    country: 'China',
    flag: '🇨🇳',
    issueDate: '22 Mar 2026',
    expiryDate: '21 Mar 2028',
    status: 'Under Review',
    trustScore: 78,
    hash: '0xHLCM-7DD3-18CA-55B2',
    recognizedMarkets: ['Malaysia', 'Indonesia'],
  },
  {
    id: 'cert-004',
    certId: 'HC-MY-2026-006814',
    company: 'Global Tayyib Foods',
    product: 'Ready-to-Eat Halal Meals',
    category: 'Halal Food & Beverage',
    certBody: 'JAKIM',
    country: 'Malaysia',
    flag: '🇲🇾',
    issueDate: '08 Jan 2026',
    expiryDate: '31 Dec 2027',
    status: 'Valid',
    trustScore: 96,
    hash: '0xHLCM-2EF8-90BD-44A1',
    recognizedMarkets: ['US', 'China', 'OIC Markets'],
  },
  {
    id: 'cert-005',
    certId: 'HC-SA-2026-003341',
    company: 'Al-Rawdah Apiaries',
    product: 'Organic Halal Honey',
    category: 'Halal Food & Beverage',
    certBody: 'SASO Recognized',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    issueDate: '11 Feb 2026',
    expiryDate: '10 Feb 2028',
    status: 'Valid',
    trustScore: 97,
    hash: '0xHLCM-5AA4-61FC-22D7',
    recognizedMarkets: ['Malaysia', 'Indonesia', 'UAE', 'UK'],
  },
  {
    id: 'cert-006',
    certId: 'HC-ID-2026-007723',
    company: 'PharmaTayyib Indonesia',
    product: 'Halal Pharmaceutical Excipients',
    category: 'Pharmaceuticals',
    certBody: 'MUI Certified',
    country: 'Indonesia',
    flag: '🇮🇩',
    issueDate: '19 Mar 2026',
    expiryDate: '18 Mar 2028',
    status: 'Valid',
    trustScore: 89,
    hash: '0xHLCM-8BC9-34DE-77F3',
    recognizedMarkets: ['Malaysia', 'GCC', 'OIC Markets'],
  },
  {
    id: 'cert-007',
    certId: 'HC-TR-2026-002198',
    company: 'Layali Modest Wear',
    product: 'Modest Fashion Collection',
    category: 'Modest Fashion',
    certBody: 'TSE Halal Certified',
    country: 'Türkiye',
    flag: '🇹🇷',
    issueDate: '05 Apr 2026',
    expiryDate: '04 Apr 2028',
    status: 'Valid',
    trustScore: 85,
    hash: '0xHLCM-1CD5-47AB-88E9',
    recognizedMarkets: ['Malaysia', 'Indonesia', 'UAE', 'UK'],
  },
  {
    id: 'cert-008',
    certId: 'HC-MY-2025-008821',
    company: 'GreenPack Halal Sdn Bhd',
    product: 'Halal Packaging Solutions',
    category: 'Packaging',
    certBody: 'JAKIM Recognized',
    country: 'Malaysia',
    flag: '🇲🇾',
    issueDate: '01 Jun 2025',
    expiryDate: '31 Aug 2026',
    status: 'Expiring Soon',
    trustScore: 82,
    hash: '0xHLCM-4EF2-99CA-31B6',
    recognizedMarkets: ['Indonesia', 'GCC'],
  },
  {
    id: 'cert-009',
    certId: 'HC-BH-2026-000812',
    company: 'Agri Tayyib Bahrain',
    product: 'Halal Agriculture Inputs',
    category: 'Agriculture',
    certBody: 'BSMS Certified',
    country: 'Bahrain',
    flag: '🇧🇭',
    issueDate: '14 Jan 2026',
    expiryDate: '13 Jan 2028',
    status: 'Valid',
    trustScore: 88,
    hash: '0xHLCM-6BA3-25FE-14C2',
    recognizedMarkets: ['GCC', 'Malaysia', 'OIC Markets'],
  },
  {
    id: 'cert-010',
    certId: 'HC-AE-2026-004401',
    company: 'Al-Nour Dairy UAE',
    product: 'Halal Dairy & Cheese Range',
    category: 'Halal Food & Beverage',
    certBody: 'ESMA Certified',
    country: 'UAE',
    flag: '🇦🇪',
    issueDate: '20 Feb 2026',
    expiryDate: '19 Feb 2028',
    status: 'Valid',
    trustScore: 95,
    hash: '0xHLCM-9DC7-53BE-62A8',
    recognizedMarkets: ['Malaysia', 'Indonesia', 'GCC', 'OIC Markets'],
  },
];

type Certificate = typeof certificates[0];
type SortKey = 'certId' | 'company' | 'country' | 'issueDate' | 'expiryDate' | 'trustScore' | 'status';

const certBodyOptions = ['All Bodies', 'JAKIM', 'JAKIM Recognized Partner', 'IFANCA', 'MUI Certified', 'ESMA Certified', 'SASO Recognized', 'TSE Halal Certified', 'China Halal Certification Partner', 'BSMS Certified', 'MUIS Certified'];
const statusOptions = ['All Status', 'Valid', 'Expiring Soon', 'Under Review', 'Suspended'];
const countryOptions = ['All Countries', 'Malaysia', 'China', 'United States', 'Indonesia', 'UAE', 'Saudi Arabia', 'Türkiye', 'Bahrain'];

export default function CertificationRegistryClient() {
  const [search, setSearch] = useState('');
  const [selectedBody, setSelectedBody] = useState('All Bodies');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [sortKey, setSortKey] = useState<SortKey>('certId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [verifyModal, setVerifyModal] = useState<Certificate | null>(null);
  const [auditModal, setAuditModal] = useState<Certificate | null>(null);
  const [page, setPage] = useState(1);
  const perPage = 8;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let result = certificates.filter((c) => {
      const matchSearch = !search || c.certId.toLowerCase().includes(search.toLowerCase()) || c.company.toLowerCase().includes(search.toLowerCase()) || c.product.toLowerCase().includes(search.toLowerCase());
      const matchBody = selectedBody === 'All Bodies' || c.certBody === selectedBody;
      const matchStatus = selectedStatus === 'All Status' || c.status === selectedStatus;
      const matchCountry = selectedCountry === 'All Countries' || c.country === selectedCountry;
      return matchSearch && matchBody && matchStatus && matchCountry;
    });
    result = [...result].sort((a, b) => {
      const av = a[sortKey as keyof Certificate];
      const bv = b[sortKey as keyof Certificate];
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return result;
  }, [search, selectedBody, selectedStatus, selectedCountry, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const certBadgeVariant = (status: string): 'valid' | 'expiring' | 'review' | 'pending' | 'suspended' => {
    if (status === 'Valid') return 'valid';
    if (status === 'Expiring Soon') return 'expiring';
    if (status === 'Under Review') return 'review';
    if (status === 'Suspended') return 'suspended';
    return 'pending';
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1" style={{ verticalAlign: 'middle' }}>
      <ChevronUp size={10} style={{ color: sortKey === col && sortDir === 'asc' ? 'var(--emerald-bright)' : 'var(--muted-foreground)', marginBottom: '-2px' }} />
      <ChevronDown size={10} style={{ color: sortKey === col && sortDir === 'desc' ? 'var(--emerald-bright)' : 'var(--muted-foreground)' }} />
    </span>
  );

  return (
    <div className="max-w-screen-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Digital Certification Registry</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            {filtered.length} certificates across 118+ recognized certification bodies
          </p>
        </div>
        <button
          onClick={() => toast.success('Registry export initiated — download ready shortly')}
          className="btn-outline flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
        >
          <Download size={15} />
          Export Registry
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded-2xl p-5 shadow-card"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          <div className="relative sm:col-span-2 xl:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by certificate ID, company, or product..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm input-field"
            />
          </div>
          <div>
            <select
              value={selectedBody}
              onChange={(e) => { setSelectedBody(e.target.value); setPage(1); }}
              className="w-full px-3 py-2.5 rounded-xl text-sm input-field"
            >
              {certBodyOptions.map((b) => <option key={`body-${b}`} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
              className="w-full px-3 py-2.5 rounded-xl text-sm input-field"
            >
              {statusOptions.map((s) => <option key={`regstatus-${s}`} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <select
              value={selectedCountry}
              onChange={(e) => { setSelectedCountry(e.target.value); setPage(1); }}
              className="w-full px-3 py-2.5 rounded-xl text-sm input-field"
            >
              {countryOptions.map((c) => <option key={`regcountry-${c}`} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-2xl shadow-card overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                {[
                  { label: 'Certificate ID', key: 'certId' as SortKey },
                  { label: 'Company', key: 'company' as SortKey },
                  { label: 'Product Category', key: null },
                  { label: 'Certification Body', key: null },
                  { label: 'Country', key: 'country' as SortKey },
                  { label: 'Issue Date', key: 'issueDate' as SortKey },
                  { label: 'Expiry Date', key: 'expiryDate' as SortKey },
                  { label: 'Status', key: 'status' as SortKey },
                  { label: 'Trust Score', key: 'trustScore' as SortKey },
                  { label: 'Actions', key: null },
                ].map((col) => (
                  <th
                    key={`th-${col.label}`}
                    className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${col.key ? 'cursor-pointer select-none hover:text-foreground' : ''}`}
                    style={{ color: 'var(--muted-foreground)', letterSpacing: '0.05em' }}
                    onClick={col.key ? () => handleSort(col.key as SortKey) : undefined}
                  >
                    {col.label}
                    {col.key && <SortIcon col={col.key as SortKey} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    No certificates match your search criteria
                  </td>
                </tr>
              ) : (
                paginated.map((cert) => (
                  <tr
                    key={cert.id}
                    className="table-row-hover"
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td className="px-4 py-3.5 font-mono-data text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--emerald-bright)' }}>
                      {cert.certId}
                    </td>
                    <td className="px-4 py-3.5 font-medium whitespace-nowrap max-w-[180px] truncate">
                      <div className="flex items-center gap-2">
                        <span>{cert.flag}</span>
                        <span className="truncate">{cert.company}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" style={{ color: 'var(--muted-foreground)' }}>
                      {cert.category}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-xs" style={{ color: 'var(--secondary-foreground)' }}>
                      {cert.certBody}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" style={{ color: 'var(--secondary-foreground)' }}>
                      {cert.country}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap font-mono-data text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {cert.issueDate}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap font-mono-data text-xs" style={{ color: cert.status === 'Expiring Soon' ? 'var(--warning)' : 'var(--muted-foreground)' }}>
                      {cert.expiryDate}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <StatusBadge variant={certBadgeVariant(cert.status)} label={cert.status} />
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
                          <div
                            className="h-1.5 rounded-full"
                            style={{
                              width: `${cert.trustScore}%`,
                              background: cert.trustScore >= 90 ? 'var(--emerald-bright)' : cert.trustScore >= 80 ? 'var(--gold)' : 'var(--warning)',
                            }}
                          />
                        </div>
                        <span className="text-xs font-semibold font-mono-data" style={{ color: 'var(--foreground)' }}>{cert.trustScore}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setVerifyModal(cert)}
                          className="p-1.5 rounded-lg transition-colors hover:bg-muted"
                          title="Verify via QR"
                          style={{ color: 'var(--emerald-bright)' }}
                        >
                          <QrCode size={15} />
                        </button>
                        <button
                          onClick={() => setAuditModal(cert)}
                          className="p-1.5 rounded-lg transition-colors hover:bg-muted"
                          title="View audit trail"
                          style={{ color: 'var(--info)' }}
                        >
                          <History size={15} />
                        </button>
                        <button
                          onClick={() => toast.success('Certificate summary downloaded')}
                          className="p-1.5 rounded-lg transition-colors hover:bg-muted"
                          title="Download certificate summary"
                          style={{ color: 'var(--muted-foreground)' }}
                        >
                          <Download size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-5 py-4 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Showing {Math.min((page - 1) * perPage + 1, filtered.length)}–{Math.min(page * perPage, filtered.length)} of {filtered.length} certificates
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-xs btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className="w-8 h-8 rounded-lg text-xs font-semibold transition-all duration-150"
                style={{
                  background: page === p ? 'var(--primary)' : 'transparent',
                  color: page === p ? 'white' : 'var(--muted-foreground)',
                  border: page === p ? 'none' : '1px solid var(--border)',
                }}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-xs btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {verifyModal && <CertVerifyModal cert={verifyModal} onClose={() => setVerifyModal(null)} />}
      {auditModal && <AuditTrailModal cert={auditModal} onClose={() => setAuditModal(null)} />}
    </div>
  );
}